import { useRef, useState, type FormEvent } from 'react';
import { t } from '../../i18n';
import { formatDate, parseDecimalInput } from '@werkdelta/shared';
import { Feld, Fehler, useConfirm } from '../../components/ui';
import { useEntityActions } from '../../hooks';
import { api, ApiError } from '../../api';
import type { StepProps } from '../CasePage';

/** Schritt 4: Istzustand — Befunde, Messungen, Fotos, Ort, Ausmass (Auftrag 7.4). */
export function Step4Ist({ caseId, data, reload, readonly }: StepProps) {
  const actions = useEntityActions(caseId, 'defect-findings', reload);
  const [form, setForm] = useState(leer());
  const { confirm, dialog } = useConfirm();

  function leer() {
    return {
      title: '',
      descriptionActual: '',
      location: '',
      extent: '',
      safetyRelevant: false,
      structuralSafetyRelevant: false,
      durabilityRelevant: false,
      discoveryDate: '',
      noticeDate: '',
      noticeRecipient: '',
      noticeContent: '',
      buildingElementId: '',
      targetRequirementId: '',
      contractRelationId: '',
    };
  }

  async function add(e: FormEvent) {
    e.preventDefault();
    const ok = await actions.create({
      ...form,
      descriptionActual: form.descriptionActual || undefined,
      location: form.location || undefined,
      extent: form.extent || undefined,
      discoveryDate: form.discoveryDate || undefined,
      noticeDate: form.noticeDate || undefined,
      noticeRecipient: form.noticeRecipient || undefined,
      noticeContent: form.noticeContent || undefined,
      buildingElementId: form.buildingElementId || undefined,
      targetRequirementId: form.targetRequirementId || undefined,
      contractRelationId: form.contractRelationId || undefined,
    });
    if (ok) setForm(leer());
  }

  return (
    <>
      {dialog}
      <section className="karte">
        <h2>{t('step4.title')}</h2>
        <p className="erklaerung">{t('step4.intro')}</p>
        {data.defectFindings.length === 0 && <div className="leer-hinweis">{t('step4.empty')}</div>}
        {data.defectFindings.map((d: any) => (
          <BefundEintrag
            key={d.id}
            defect={d}
            caseId={caseId}
            reload={reload}
            readonly={readonly}
            onDelete={async () => {
              if (await confirm(t('step4.deleteConfirm', { title: d.title }), true)) {
                await actions.remove(d.id);
              }
            }}
          />
        ))}
      </section>

      {!readonly && (
        <section className="karte">
          <h3>{t('step4.add')}</h3>
          <form onSubmit={add}>
            <Feld label={t('step4.defectTitle')} required error={actions.fieldErrors['title']}>
              <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required />
            </Feld>
            <Feld label={t('step4.actual')} hint={t('step4.actualHint')}>
              <textarea
                value={form.descriptionActual}
                onChange={(e) => setForm({ ...form, descriptionActual: e.target.value })}
              />
            </Feld>
            <div className="feld-zeile">
              <Feld label={t('step4.location')}>
                <input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} />
              </Feld>
              <Feld label={t('step4.extent')}>
                <input value={form.extent} onChange={(e) => setForm({ ...form, extent: e.target.value })} />
              </Feld>
            </div>
            <fieldset>
              <legend>{t('step4.flags')}</legend>
              <div className="kontrollzeile">
                <input
                  id="flag-sicherheit"
                  type="checkbox"
                  checked={form.safetyRelevant}
                  onChange={(e) => setForm({ ...form, safetyRelevant: e.target.checked })}
                />
                <label htmlFor="flag-sicherheit">{t('step4.safetyRelevant')}</label>
              </div>
              <div className="kontrollzeile">
                <input
                  id="flag-standsicherheit"
                  type="checkbox"
                  checked={form.structuralSafetyRelevant}
                  onChange={(e) => setForm({ ...form, structuralSafetyRelevant: e.target.checked })}
                />
                <label htmlFor="flag-standsicherheit">{t('step4.structuralRelevant')}</label>
              </div>
              <div className="kontrollzeile">
                <input
                  id="flag-dauer"
                  type="checkbox"
                  checked={form.durabilityRelevant}
                  onChange={(e) => setForm({ ...form, durabilityRelevant: e.target.checked })}
                />
                <label htmlFor="flag-dauer">{t('step4.durabilityRelevant')}</label>
              </div>
              {(form.safetyRelevant || form.structuralSafetyRelevant) && (
                <p className="warnung">{t('safety.confirmation.required')}</p>
              )}
            </fieldset>
            <fieldset>
              <legend>{t('step4.timeline')}</legend>
              <div className="feld-zeile">
                <Feld label={t('step4.discovery')}>
                  <input
                    type="date"
                    value={form.discoveryDate}
                    onChange={(e) => setForm({ ...form, discoveryDate: e.target.value })}
                  />
                </Feld>
                <Feld label={t('step4.notice')}>
                  <input
                    type="date"
                    value={form.noticeDate}
                    onChange={(e) => setForm({ ...form, noticeDate: e.target.value })}
                  />
                </Feld>
                <Feld label={t('step4.noticeRecipient')}>
                  <input
                    value={form.noticeRecipient}
                    onChange={(e) => setForm({ ...form, noticeRecipient: e.target.value })}
                  />
                </Feld>
              </div>
              <Feld label={t('step4.noticeContent')}>
                <textarea
                  value={form.noticeContent}
                  onChange={(e) => setForm({ ...form, noticeContent: e.target.value })}
                />
              </Feld>
            </fieldset>
            <div className="feld-zeile">
              <Feld label={t('step3.elementRef')}>
                <select
                  value={form.buildingElementId}
                  onChange={(e) => setForm({ ...form, buildingElementId: e.target.value })}
                >
                  <option value="">—</option>
                  {data.buildingElements.map((el: any) => (
                    <option key={el.id} value={el.id}>{el.name}</option>
                  ))}
                </select>
              </Feld>
              <Feld label={t('step4.requirementRef')}>
                <select
                  value={form.targetRequirementId}
                  onChange={(e) => setForm({ ...form, targetRequirementId: e.target.value })}
                >
                  <option value="">—</option>
                  {data.targetRequirements.map((r: any) => (
                    <option key={r.id} value={r.id}>{r.description.slice(0, 60)}</option>
                  ))}
                </select>
              </Feld>
              <Feld label={t('step3.relationRef')}>
                <select
                  value={form.contractRelationId}
                  onChange={(e) => setForm({ ...form, contractRelationId: e.target.value })}
                >
                  <option value="">—</option>
                  {data.contractRelations.map((rel: any) => (
                    <option key={rel.id} value={rel.id}>{rel.label}</option>
                  ))}
                </select>
              </Feld>
            </div>
            <Fehler code={actions.errorCode} />
            <div className="aktionen">
              <button type="submit" className="knopf-primaer">{t('common.add')}</button>
            </div>
          </form>
        </section>
      )}
    </>
  );
}

function BefundEintrag({
  defect,
  caseId,
  reload,
  readonly,
  onDelete,
}: {
  defect: any;
  caseId: string;
  reload: () => void;
  readonly: boolean;
  onDelete: () => void;
}) {
  return (
    <div className="eintrag">
      <div className="eintrag-kopf">
        <strong>{defect.title}</strong>
        <span style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap' }}>
          {defect.safetyRelevant && <span className="badge warn">{t('step4.badge.safety')}</span>}
          {defect.structuralSafetyRelevant && (
            <span className="badge warn">{t('step4.badge.structural')}</span>
          )}
          {defect.durabilityRelevant && <span className="badge">{t('step4.badge.durability')}</span>}
          {!readonly && (
            <button type="button" className="knopf-sekundaer knopf-klein" onClick={onDelete}>
              {t('common.delete')}
            </button>
          )}
        </span>
      </div>
      {defect.descriptionActual && <p>{defect.descriptionActual}</p>}
      <p className="meta">
        {[
          defect.location,
          defect.extent,
          defect.discoveryDate ? `${t('step4.discovery')}: ${formatDate(defect.discoveryDate)}` : null,
          defect.noticeDate ? `${t('step4.notice')}: ${formatDate(defect.noticeDate)}` : null,
        ]
          .filter(Boolean)
          .join(' · ')}
      </p>
      <MessungenBlock defect={defect} caseId={caseId} reload={reload} readonly={readonly} />
      <BeweismittelBlock defect={defect} caseId={caseId} reload={reload} readonly={readonly} />
    </div>
  );
}

function MessungenBlock({
  defect,
  caseId,
  reload,
  readonly,
}: {
  defect: any;
  caseId: string;
  reload: () => void;
  readonly: boolean;
}) {
  const actions = useEntityActions(caseId, 'measurements', reload);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: '', value: '', unit: '', method: '', device: '', uncertainty: '' });
  const [valueError, setValueError] = useState<string | undefined>();

  async function add(e: FormEvent) {
    e.preventDefault();
    const parsed = parseDecimalInput(form.value);
    if (parsed === null) {
      setValueError(t('input.decimalInvalid'));
      return;
    }
    setValueError(undefined);
    const ok = await actions.create({
      defectFindingId: defect.id,
      name: form.name,
      value: parsed,
      unit: form.unit,
      method: form.method || undefined,
      device: form.device || undefined,
      uncertainty: form.uncertainty || undefined,
    });
    if (ok) setForm({ name: '', value: '', unit: '', method: '', device: '', uncertainty: '' });
  }

  return (
    <div style={{ marginTop: '0.6rem' }}>
      <h4 style={{ margin: '0.4rem 0' }}>{t('step4.measurements')}</h4>
      {defect.measurements.length === 0 ? (
        <p className="meta">{t('step4.measurementsEmpty')}</p>
      ) : (
        <div className="tabelle-scroll">
        <table className="daten">
          <thead>
            <tr>
              <th>{t('step4.measurementName')}</th>
              <th className="zahl">{t('step4.measurementValue')}</th>
              <th>{t('step4.measurementUnit')}</th>
              <th>{t('step4.measurementMethod')}</th>
              <th>{t('step4.measurementUncertainty')}</th>
            </tr>
          </thead>
          <tbody>
            {defect.measurements.map((m: any) => (
              <tr key={m.id}>
                <td>{m.name}</td>
                <td className="zahl">{m.value}</td>
                <td>{m.unit}</td>
                <td>{[m.method, m.device].filter(Boolean).join(', ')}</td>
                <td>{m.uncertainty ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      )}
      {!readonly && !open && (
        <button type="button" className="knopf-sekundaer knopf-klein" style={{ marginTop: '0.4rem' }} onClick={() => setOpen(true)}>
          {t('step4.addMeasurement')}
        </button>
      )}
      {!readonly && open && (
        <form onSubmit={add} style={{ marginTop: '0.5rem' }}>
          <div className="feld-zeile">
            <Feld label={t('step4.measurementName')} required>
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
            </Feld>
            <Feld label={t('step4.measurementValue')} required error={valueError} hint={t('input.decimalHint')}>
              <input value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} required />
            </Feld>
            <Feld label={t('step4.measurementUnit')} required>
              <input value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} required />
            </Feld>
          </div>
          <div className="feld-zeile">
            <Feld label={t('step4.measurementMethod')}>
              <input value={form.method} onChange={(e) => setForm({ ...form, method: e.target.value })} />
            </Feld>
            <Feld label={t('step4.measurementDevice')}>
              <input value={form.device} onChange={(e) => setForm({ ...form, device: e.target.value })} />
            </Feld>
            <Feld label={t('step4.measurementUncertainty')} hint={t('step4.uncertaintyHint')}>
              <input value={form.uncertainty} onChange={(e) => setForm({ ...form, uncertainty: e.target.value })} />
            </Feld>
          </div>
          <Fehler code={actions.errorCode} />
          <div className="aktionen">
            <button type="submit" className="knopf-primaer knopf-klein">{t('common.add')}</button>
            <button type="button" className="knopf-sekundaer knopf-klein" onClick={() => setOpen(false)}>
              {t('common.close')}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

function BeweismittelBlock({
  defect,
  caseId,
  reload,
  readonly,
}: {
  defect: any;
  caseId: string;
  reload: () => void;
  readonly: boolean;
}) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [keepGps, setKeepGps] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function upload() {
    const file = fileRef.current?.files?.[0];
    if (!file) return;
    setError(null);
    setBusy(true);
    try {
      await api.upload(
        `/api/v1/cases/${caseId}/evidence?defectFindingId=${defect.id}&keepGps=${keepGps}`,
        file,
      );
      if (fileRef.current) fileRef.current.value = '';
      reload();
    } catch (e) {
      setError(e instanceof ApiError ? e.code : 'INTERNAL');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{ marginTop: '0.6rem' }}>
      <h4 style={{ margin: '0.4rem 0' }}>{t('step4.evidence')}</h4>
      {defect.evidences.length === 0 ? (
        <p className="meta">{t('step4.evidenceEmpty')}</p>
      ) : (
        <ul>
          {defect.evidences.map((ev: any) => (
            <li key={ev.id}>
              <a href={`/api/v1/cases/${caseId}/evidence/${ev.id}/file`}>{ev.fileName}</a>{' '}
              <span className="meta">
                ({Math.round(ev.sizeBytes / 1024)} kB
                {ev.captureDate ? ` · ${t('step4.captured')} ${formatDate(ev.captureDate)}` : ''}
                {ev.gpsRemoved ? ` · ${t('step4.gpsRemoved')}` : ''}
                {ev.gpsKeptByChoice ? ` · ${t('step4.gpsKept')}` : ''})
              </span>
            </li>
          ))}
        </ul>
      )}
      {!readonly && (
        <div>
          <input ref={fileRef} type="file" aria-label={t('step4.chooseFile')} accept=".pdf,.jpg,.jpeg,.png,.heic,.docx,.xlsx,.pptx" />
          <div className="kontrollzeile">
            <input
              id={`gps-${defect.id}`}
              type="checkbox"
              checked={keepGps}
              onChange={(e) => setKeepGps(e.target.checked)}
            />
            <label htmlFor={`gps-${defect.id}`}>{t('step4.keepGps')}</label>
          </div>
          <p className="erklaerung">{t('step4.gpsDefaultNote')}</p>
          <Fehler code={error} />
          <button type="button" className="knopf-sekundaer knopf-klein" onClick={upload} disabled={busy}>
            {t('step4.upload')}
          </button>
        </div>
      )}
    </div>
  );
}
